import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, TrendingDown, Activity, RefreshCw, Settings,
  BookOpen, BarChart2, Search, Crosshair
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend
} from 'recharts';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const spin = keyframes`from{transform:rotate(0)}to{transform:rotate(360deg)}`;

const Page = styled.div`min-height:100vh;background:#0a0a0f;color:#e2e8f0;font-family:'Inter','Pretendard',sans-serif;padding:20px;`;
const TopBar = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;&:hover{background:#1e2030;color:#e2e8f0}`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;background:linear-gradient(135deg,#8b5cf6,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;

const NavTabs = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const NavTab = styled.button`
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;
  &:hover{border-color:#8b5cf6;color:#8b5cf6}
`;

const StatusBadge = styled.span`
  display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;
  padding:4px 10px;border-radius:20px;
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'rgba(100,116,139,0.15)'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#374151'};
`;
const PulseDot = styled.span`width:7px;height:7px;border-radius:50%;background:${p => p.active ? '#8b5cf6' : '#64748b'};animation:${p => p.active ? pulse : 'none'} 1.5s infinite;`;

const StatGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:14px;margin-bottom:28px;animation:${fadeIn} .4s ease;`;
const StatCard = styled.div`
  background:#111827;border:1px solid ${p => p.highlight ? p.color || '#8b5cf6' : '#1e2030'};
  border-radius:14px;padding:18px 20px;transition:all .2s;
  box-shadow:${p => p.highlight ? `0 0 20px ${p.color || '#8b5cf6'}22` : 'none'};
`;
const StatLabel = styled.p`margin:0 0 8px;font-size:11px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:.05em;`;
const StatVal = styled.div`font-size:24px;font-weight:800;color:${p => p.positive === true ? '#10b981' : p.positive === false ? '#ef4444' : '#e2e8f0'};`;
const StatSub = styled.p`margin:4px 0 0;font-size:11px;color:#475569;`;

const ChartWrap = styled.div`background:#111827;border:1px solid #1e2030;border-radius:16px;padding:24px;margin-bottom:24px;animation:${fadeIn} .5s ease;`;
const ChartTitle = styled.h3`margin:0 0 20px;font-size:14px;font-weight:700;color:#94a3b8;`;

const SignalList = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px;margin-bottom:28px;animation:${fadeIn} .6s ease;`;
const SignalCard = styled.div`
  background:#111827;border-radius:12px;padding:16px;transition:all .2s;
  border:1px solid ${p =>
    p.signal === 'VCP_BREAKOUT' ? '#10b981' :
    p.signal === 'VCP_READY' ? '#f59e0b' :
    p.signal === 'VCP_FORMING' ? '#06b6d4' :
    p.signal === 'TEMPLATE_PASS' ? '#8b5cf6' :
    p.signal === 'HOLD' ? '#64748b' :
    p.signal === 'SELL_STOP_LOSS' || p.signal === 'SELL_TRAILING' ? '#ef4444' :
    '#1e2030'};
  &:hover{transform:translateY(-2px)}
`;
const Badge = styled.span`
  display:inline-block;font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;
  background:${p =>
    p.type === 'VCP_BREAKOUT' ? 'rgba(16,185,129,0.15)' :
    p.type === 'VCP_READY' ? 'rgba(245,158,11,0.15)' :
    p.type === 'VCP_FORMING' ? 'rgba(6,182,212,0.15)' :
    p.type === 'TEMPLATE_PASS' ? 'rgba(139,92,246,0.15)' :
    p.type?.startsWith('SELL') ? 'rgba(239,68,68,0.15)' :
    'rgba(100,116,139,0.15)'};
  color:${p =>
    p.type === 'VCP_BREAKOUT' ? '#10b981' :
    p.type === 'VCP_READY' ? '#f59e0b' :
    p.type === 'VCP_FORMING' ? '#06b6d4' :
    p.type === 'TEMPLATE_PASS' ? '#8b5cf6' :
    p.type?.startsWith('SELL') ? '#ef4444' :
    '#64748b'};
`;

const ActionBar = styled.div`display:flex;gap:10px;flex-wrap:wrap;margin-bottom:24px;`;
const ActionBtn = styled.button`
  display:flex;align-items:center;gap:7px;padding:10px 18px;border-radius:10px;font-size:13px;font-weight:600;
  cursor:pointer;transition:all .2s;border:none;
  background:${p => p.danger ? 'rgba(239,68,68,0.1)' : p.primary ? 'rgba(139,92,246,0.15)' : '#1e2030'};
  color:${p => p.danger ? '#ef4444' : p.primary ? '#8b5cf6' : '#94a3b8'};
  border:1px solid ${p => p.danger ? '#ef4444' : p.primary ? '#8b5cf6' : '#374151'};
  &:hover{transform:translateY(-1px);opacity:.9}
  &:disabled{opacity:.4;cursor:not-allowed;transform:none}
`;
const SpinIcon = styled(RefreshCw)`animation:${p => p.spinning ? css`${spin} 1s linear infinite` : 'none'};`;

const ScanPanel = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;padding:16px 20px;margin-bottom:24px;
  animation:${fadeIn} .3s ease;
`;
const ScanHeader = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;`;
const ScanHeaderLeft = styled.div`display:flex;align-items:center;gap:8px;font-size:13px;color:#94a3b8;`;
const scanPulse = keyframes`0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.3);opacity:.6}`;
const ScanSpinner = styled.div`width:8px;height:8px;border-radius:50%;background:#8b5cf6;animation:${scanPulse} 1s ease infinite;`;
const ScanCounter = styled.div`font-size:12px;font-weight:700;color:#8b5cf6;`;
const ScanTrack = styled.div`height:4px;background:#0d1117;border-radius:2px;overflow:hidden;margin-bottom:14px;`;
const ScanFill = styled.div`height:100%;background:linear-gradient(90deg,#8b5cf6,#06b6d4);border-radius:2px;transition:width .4s;`;
const ScanList = styled.div`display:flex;flex-direction:column;gap:6px;max-height:320px;overflow-y:auto;`;
const ScanItem = styled.div`
  display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:12px;
  padding:8px 12px;border-radius:8px;font-size:12px;
  background:${p =>
    p.status === 'buy' ? 'rgba(16,185,129,0.07)' :
    p.status === 'sell' ? 'rgba(239,68,68,0.07)' :
    p.status === 'hold' ? 'rgba(139,92,246,0.07)' :
    '#0d1117'};
  border-left:3px solid ${p =>
    p.status === 'buy' ? '#10b981' :
    p.status === 'sell' ? '#ef4444' :
    p.status === 'hold' ? '#8b5cf6' :
    '#1e2030'};
`;
const ScanItemName = styled.div`font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const ScanItemBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 8px;border-radius:5px;white-space:nowrap;
  background:${p =>
    p.type === 'VCP_BREAKOUT' ? 'rgba(16,185,129,0.15)' :
    p.type === 'VCP_READY' ? 'rgba(245,158,11,0.15)' :
    p.type === 'VCP_FORMING' ? 'rgba(6,182,212,0.15)' :
    p.type === 'HOLD' ? 'rgba(139,92,246,0.15)' :
    p.type?.startsWith('SELL') ? 'rgba(239,68,68,0.15)' :
    'rgba(100,116,139,0.12)'};
  color:${p =>
    p.type === 'VCP_BREAKOUT' ? '#10b981' :
    p.type === 'VCP_READY' ? '#f59e0b' :
    p.type === 'VCP_FORMING' ? '#06b6d4' :
    p.type === 'HOLD' ? '#8b5cf6' :
    p.type?.startsWith('SELL') ? '#ef4444' :
    '#64748b'};
`;
const ScanItemReason = styled.div`font-size:11px;color:#475569;white-space:nowrap;text-align:right;`;


const SIGNAL_LABEL = {
  VCP_BREAKOUT: '🟢 VCP 돌파 매수', VCP_READY: '🟡 VCP 대기', VCP_FORMING: '🔵 VCP 형성중',
  TEMPLATE_PASS: '⚪ 템플릿 통과', TEMPLATE_FAIL: '❌ 템플릿 미달',
  SELL_STOP_LOSS: '🔴 손절', SELL_TRAILING: '🟠 추적 손절', SELL_PROFIT: '⚠️ 이격 과다',
  HOLD: '🔷 보유중', MAX_POSITIONS: '⚪ 포지션 초과', NO_DATA: '❌ 데이터없음', ERROR: '❌ 오류',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e2030', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px' }}>
      <p style={{ margin: '0 0 4px', fontSize: 11, color: '#64748b' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0', fontSize: 13, fontWeight: 700, color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? `${p.value > 0 ? '+' : ''}${p.value.toFixed(2)}%` : p.value}
        </p>
      ))}
    </div>
  );
};

function MinerviniDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [signals, setSignals] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [lastScreenerAt, setLastScreenerAt] = useState(null);
  const [lastScanAt, setLastScanAt] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, perfRes, cfgRes] = await Promise.all([
        apiService.getMinerviniStats(),
        apiService.getMinerviniPerformance(40),
        apiService.getMinerviniConfig(),
      ]);
      setStats(statsRes); setPerformance(perfRes); setConfig(cfgRes);
      
      if (cfgRes.lastScreenerAt) setLastScreenerAt(cfgRes.lastScreenerAt);
      if (cfgRes.lastScanAt) setLastScanAt(cfgRes.lastScanAt);
      
    } catch (err) { console.error('loadData error:', err); }
    finally { setLoading(false); }
  }, []);

  // ─── Batch Watcher Logic (Frontend-driven) ───────────────────
  useEffect(() => {
    if (!config?.isActive && !config?.useAutoScreener) return;

    const interval = setInterval(async () => {
      const now = new Date();
      const day = now.getDay();
      if (day === 0 || day === 6) return; // Skip weekends

      const timeStr = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
      const todayStr = now.toISOString().split('T')[0];

      // 1. Morning Screener (08:50)
      if (config.useAutoScreener && timeStr >= '08:50' && timeStr < '09:00') {
        const lastDate = lastScreenerAt?.split('T')[0];
        if (lastDate !== todayStr && !running) {
          console.log('[BatchWatcher] 📐 Triggering Minervini Morning Screener...');
          try {
            await apiService.runMinerviniScreener();
            const newCfg = await apiService.getMinerviniConfig();
            setLastScreenerAt(newCfg.lastScreenerAt);
            alert('✅ [자동] 미너비니 모닝 스크리너가 완료되었습니다.');
          } catch (e) { console.error('Auto Screener Error:', e); }
        }
      }

      // 2. Afternoon Signal Scan (15:20)
      if (config.isActive && timeStr >= '15:20' && timeStr < '15:35') {
        const lastDate = lastScanAt?.split('T')[0];
        if (lastDate !== todayStr && !running) {
          console.log('[BatchWatcher] 📐 Triggering Minervini Signal Scan...');
          try {
            await apiService.runMinerviniScan();
            const newCfg = await apiService.getMinerviniConfig();
            setLastScanAt(newCfg.lastScanAt);
            await loadData();
            alert('✅ [자동] 미너비니 장마감 신호 스캔이 완료되었습니다.');
          } catch (e) { console.error('Auto Scan Error:', e); }
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [config, lastScreenerAt, lastScanAt, running, loadData]);

  useEffect(() => { loadData(); }, [loadData]);

  const [scanProgress, setScanProgress] = useState(null); // { current, total, symbol, name }
  const [scanItems, setScanItems] = useState([]); // [{ symbol, name, actions }]

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    setScanProgress({ current: 0, total: 0, symbol: '', name: '', status: 'connecting' });
    setScanItems([]);
    try {
      await apiService.runMinerviniScan((data) => {
        if (data.type === 'progress') {
          setScanProgress({ current: data.current, total: data.total, symbol: data.symbol, name: data.name, status: 'scanning' });
        } else if (data.type === 'result') {
          setScanItems(prev => [...prev, { symbol: data.symbol, name: data.name, actions: data.actions || [] }]);
        } else if (data.type === 'done') {
          setLastScan(data.result);
          setScanProgress(prev => prev ? { ...prev, status: 'done' } : null);
          loadData();
        } else if (data.type === 'error') {
          setScanProgress(prev => prev ? { ...prev, status: 'error', errorMsg: data.message } : null);
        }
      });
    } catch (err) {
      setScanProgress(prev => prev ? { ...prev, status: 'error', errorMsg: err.message } : null);
    }
    finally { setRunning(false); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try { const res = await apiService.getMinerviniSignalPreview(); setSignals(res); }
    catch (err) { alert('미리보기 오류: ' + err.message); }
    finally { setPreviewLoading(false); }
  };

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const pct = n => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>📐 Minervini VCP Trading</PageTitle>
          <PageSub>Mark Minervini VCP 전략 기반 가상 자동매매 | 3단계 필터 + 피벗 돌파</PageSub>
        </TitleArea>
        <StatusBadge active={config?.isActive}>
          <PulseDot active={config?.isActive} />
          {config?.isActive ? '자동매매 실행 중' : '비활성'}
        </StatusBadge>
        <NavTabs>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '12px' }}>
            {lastScreenerAt && (
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                최근 스크리닝: {new Date(lastScreenerAt).toLocaleString()}
              </div>
            )}
            {lastScanAt && (
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                최근 신호스캔: {new Date(lastScanAt).toLocaleString()}
              </div>
            )}
          </div>
          <NavTab active onClick={() => navigate('/labs/minervini')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <ActionBar>
        <ActionBtn primary onClick={handleRun} disabled={running}>
          <SpinIcon size={14} spinning={running ? 1 : 0} />
          {running ? '스캔 중...' : '수동 신호 스캔'}
        </ActionBtn>
        <ActionBtn onClick={handlePreview} disabled={previewLoading}>
          <Activity size={14} />
          {previewLoading ? '조회 중...' : '현재 신호 미리보기'}
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/minervini/screener')}>
          <Crosshair size={14} />
          VCP 스크리너
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/minervini/positions')}>
          <BarChart2 size={14} />
          포지션 현황
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/minervini/journal')}>
          <BookOpen size={14} />
          거래 일지
        </ActionBtn>
      </ActionBar>

      {/* ── 실시간 신호 스캔 패널 ── */}
      {(running || scanProgress || scanItems.length > 0) && (
        <ScanPanel>
          {/* 상태 헤더 */}
          <ScanHeader>
            <ScanHeaderLeft>
              {running ? <ScanSpinner /> : scanProgress?.status === 'done' ? <span>✅</span> : <span>⚠️</span>}
              {running && scanProgress?.status === 'connecting' && (
                <span style={{ color: '#94a3b8' }}>서버에 연결 중...</span>
              )}
              {running && scanProgress?.status === 'scanning' && (
                <span>스캔 중&nbsp;<b style={{ color: '#e2e8f0' }}>{scanProgress.name || scanProgress.symbol}</b>
                  <span style={{ color: '#64748b', fontSize: 11, marginLeft: 6 }}>{scanProgress.symbol}</span>
                </span>
              )}
              {!running && scanProgress?.status === 'done' && (
                <span style={{ color: '#10b981', fontWeight: 700 }}>스캔 완료 — {scanItems.length}개 종목 처리됨</span>
              )}
              {!running && scanProgress?.status === 'error' && (
                <span style={{ color: '#ef4444' }}>오류: {scanProgress.errorMsg}</span>
              )}
              {!running && !scanProgress && scanItems.length > 0 && (
                <span style={{ color: '#64748b' }}>마지막 스캔 결과 ({scanItems.length}개 종목)</span>
              )}
            </ScanHeaderLeft>
            {scanProgress?.total > 0 && (
              <ScanCounter>{scanProgress.current} / {scanProgress.total}</ScanCounter>
            )}
          </ScanHeader>

          {/* 진행바 */}
          {running && scanProgress?.total > 0 && (
            <ScanTrack>
              <ScanFill style={{ width: `${(scanProgress.current / scanProgress.total) * 100}%` }} />
            </ScanTrack>
          )}

          {/* 결과 목록 */}
          {scanItems.length > 0 && (
            <ScanList>
              {[...scanItems].reverse().map((item, i) => {
                const action = item.actions?.[0] || {};
                const type = action.type || 'SKIP';
                const isBuy = type === 'VCP_BREAKOUT';
                const isSell = type?.startsWith('SELL');
                const isHold = type === 'HOLD';
                return (
                  <ScanItem key={`${item.symbol}-${i}`} status={isBuy ? 'buy' : isSell ? 'sell' : isHold ? 'hold' : 'skip'}>
                    <ScanItemName>
                      <b>{item.name}</b>
                      <span style={{ color: '#475569', marginLeft: 6, fontSize: 11 }}>{item.symbol}</span>
                    </ScanItemName>
                    <ScanItemBadge type={type}>{SIGNAL_LABEL[type] || type}</ScanItemBadge>
                    <ScanItemReason>
                      {isBuy && action.price && `@ ${Number(action.price).toLocaleString()}원 | VCP ${action.tCount}T 돌파`}
                      {isSell && `PnL: ${action.pnl != null ? action.pnl + '%' : '-'}`}
                      {isHold && '보유 지속'}
                      {type === 'MAX_POSITIONS' && '포지션 한도 초과'}
                      {type === 'TEMPLATE_FAIL' && `TT점수: ${action.score ?? '-'}`}
                      {type === 'TEMPLATE_PASS' && `VCP 없음 — ${action.vcpReason || '-'}`}
                      {type === 'VCP_FORMING' && `VCP ${action.tCount}T | VDU ${action.vduRatio != null ? action.vduRatio + 'x' : '-'}`}
                      {type === 'VCP_READY' && `피벗 ${action.pivotPoint ? Number(action.pivotPoint).toLocaleString() + '원' : '-'} | ${action.tCount}T`}
                      {type === 'NO_DATA' && (action.reason || '데이터 없음')}
                      {type === 'ERROR' && (action.reason || '오류 발생')}
                    </ScanItemReason>
                  </ScanItem>
                );
              })}
            </ScanList>
          )}
        </ScanPanel>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>데이터 로딩 중...</div>
      ) : (
        <>
          <StatGrid>
            <StatCard highlight color="#8b5cf6">
              <StatLabel>💰 총 평가금액</StatLabel>
              <StatVal>{fmt(stats?.totalEquity)}원</StatVal>
              <StatSub>초기: {fmt(stats?.initialEquity)}원</StatSub>
            </StatCard>
            <StatCard highlight={stats?.cumulativeReturn > 0} color={stats?.cumulativeReturn > 0 ? '#10b981' : '#ef4444'}>
              <StatLabel>📈 누적 수익률</StatLabel>
              <StatVal positive={stats?.cumulativeReturn > 0 ? true : stats?.cumulativeReturn < 0 ? false : undefined}>
                {pct(stats?.cumulativeReturn)}
              </StatVal>
              <StatSub>총 손익: {fmt(stats?.totalPnl)}원</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>🏆 승률</StatLabel>
              <StatVal positive={parseFloat(stats?.winRate) >= 50}>{stats?.winRate}%</StatVal>
              <StatSub>{stats?.wins}승 {stats?.losses}패 (총 {stats?.totalTrades}건)</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>📉 최대 낙폭 (MDD)</StatLabel>
              <StatVal positive={false}>-{stats?.maxDrawdown}%</StatVal>
              <StatSub>손익비: {stats?.profitFactor}x</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>🎯 평균 수익/손실</StatLabel>
              <StatVal style={{ fontSize: 18 }}>
                <span style={{ color: '#10b981' }}>+{stats?.avgWinRate}%</span>
                {' / '}
                <span style={{ color: '#ef4444' }}>-{stats?.avgLossRate}%</span>
              </StatVal>
              <StatSub>감시종목: {stats?.watchlistCount || 0}개</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>📊 오픈 포지션</StatLabel>
              <StatVal>{stats?.openPositionsCount || 0}개</StatVal>
              <StatSub>최대: {config?.maxPositions || 8}개</StatSub>
            </StatCard>
          </StatGrid>

          {performance.length > 0 && (
            <ChartWrap>
              <ChartTitle>📊 누적 수익률 곡선</ChartTitle>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={performance} margin={{ left: -10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={v => v?.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="#374151" strokeDasharray="4 4" />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                  <Line type="monotone" dataKey="cumulativeReturn" name="누적 수익률" stroke="#8b5cf6" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="dailyReturn" name="일별 수익률" stroke="#f59e0b" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </ChartWrap>
          )}

          {signals && (
            <>
              <ChartTitle style={{ marginBottom: 14, color: '#94a3b8', fontSize: 14 }}>
                🔍 현재 신호 미리보기 ({signals.date})
              </ChartTitle>
              <SignalList>
                {signals.signals?.map(s => (
                  <SignalCard key={s.symbol} signal={s.signal}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.symbol}</div>
                      </div>
                      <Badge type={s.signal}>{SIGNAL_LABEL[s.signal] || s.signal}</Badge>
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      <span>현재가: <b style={{ color: '#e2e8f0' }}>{fmt(s.todayClose)}원</b></span>
                      {s.templateScore != null && <span style={{ marginLeft: 10 }}>TT: <b style={{ color: '#8b5cf6' }}>{s.templateScore}점</b></span>}
                      {s.rs != null && <span style={{ marginLeft: 10 }}>RS: <b style={{ color: '#f59e0b' }}>{s.rs}</b></span>}
                    </div>
                    {s.tCount && (
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 6, lineHeight: 1.6 }}>
                        VCP: <b style={{ color: '#06b6d4' }}>{s.tCount}T</b>
                        {s.pivotPoint && <> | 피벗: <b>{fmt(s.pivotPoint)}</b></>}
                        {s.tightness && <> | 타이트니스: <b>{s.tightness}%</b></>}
                        {s.isPowerPlay && <span style={{ color: '#f59e0b', marginLeft: 6 }}>⚡ Power Play</span>}
                      </div>
                    )}
                  </SignalCard>
                ))}
              </SignalList>
            </>
          )}

          {lastScan?.log && (
            <ChartWrap>
              <ChartTitle>📋 마지막 스캔 로그</ChartTitle>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b', lineHeight: 1.8 }}>
                {lastScan.log.map((line, i) => (
                  <div key={i} style={{
                    color: line.includes('[BUY]') ? '#10b981' :
                           line.includes('[STOP]') || line.includes('[ERROR]') ? '#ef4444' :
                           line.includes('[READY]') ? '#f59e0b' :
                           line.includes('[HOLD]') ? '#8b5cf6' : '#64748b'
                  }}>{line}</div>
                ))}
              </div>
            </ChartWrap>
          )}
        </>
      )}
    </Page>
  );
}

export default MinerviniDashboardPage;
